import React, { FC, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Schema } from 'yup';
import cardValidator from 'card-validator';
import defaults from 'lodash/defaults';

import { config, yup } from 'data';
import { formatter, url } from 'helpers';
import { paymentService } from 'services';
import { useAuth, useForm, useFormWatch, useLang, useModal, useMutation, useQuery } from 'hooks';
import { BaseLayout } from 'layouts';
import { Logo } from 'components/icons';
import { Button, Card, Container, Form, Input, Select, Spin, TextArea } from 'components/ui';
import { Nullish } from 'types/common';
import { ModalBaseProps } from 'types/components';
import { InvoiceBasic, PaymentMethod, PaymentStatus } from 'types/models';
import { PaymentCardCreateParams, PaymentCreateBaseParams, PaymentFrancophoneCreateParams } from 'types/services';

import styles from './styles.module.css';

import AvsVerificationModal from './AvsVerificationModal';
import OtpVerificationModal from './OtpVerificationModal';
import PinVerificationModal from './PinVerificationModal';
import ErrorModal from './ErrorModal';
import SuccessModal from './SuccessModal';

const paymentMethods = Object.values(PaymentMethod);

type CardFormValues = Omit<PaymentCardCreateParams, 'invoiceId' | 'expiry_month' | 'expiry_year'> & {
  expiry: string;
};

type FrancophoneFormValues = Omit<PaymentFrancophoneCreateParams, 'invoiceId'>;

type FormValues = Omit<PaymentCreateBaseParams, 'invoiceId'> & ({
  method: PaymentMethod.CARD;
  details: Omit<CardFormValues, keyof PaymentCreateBaseParams>;
} | {
  method: PaymentMethod.FRANCOPHONE;
  details: Omit<FrancophoneFormValues, keyof PaymentCreateBaseParams>;
});

const initialValues: Omit<FormValues, 'details'> = {
  method: PaymentMethod.CARD,
  email: '',
};

const cardValidationSchema = {
  card_number: yup.string().required().cardNumber(),
  expiry: yup.string().required().cardExpiry(),
  cvv: yup.string().required().cardCode(),
};

const francophoneValidationSchema = {
  phone: yup.string().required().phone(),
};

const validationSchema = yup.object({
  method: yup.string().required().oneOf(paymentMethods),
  email: yup.string().required().email(),
  purpose: yup.string().notRequired().max(config.PAYMENT_PURPOSE_MAX_LENGTH),
  details: yup.object().when('method', ([method], schema) => {
    switch (method) {
      case PaymentMethod.CARD:
        return schema.shape(cardValidationSchema);
      case PaymentMethod.FRANCOPHONE:
        return schema.shape(francophoneValidationSchema);
      default:
        return schema;
    }
  }),
});

type ModalProps = ModalBaseProps & {
  trxRef: string;
  error?: Nullish<string>;
  pending?: boolean;
  onSuccess?: VoidFunction;
};

const modals: Record<PaymentStatus, FC<ModalProps> | null> = {
  [PaymentStatus.AWAITING_3DS]: null,
  [PaymentStatus.AWAITING_AVS]: AvsVerificationModal,
  [PaymentStatus.AWAITING_CONFIRMATION]: SuccessModal,
  [PaymentStatus.AWAITING_OTP]: OtpVerificationModal,
  [PaymentStatus.AWAITING_PIN]: PinVerificationModal,
  [PaymentStatus.CREATED]: null,
  [PaymentStatus.ERROR]: ErrorModal,
  [PaymentStatus.PENDING]: null,
  [PaymentStatus.SUCCESS]: SuccessModal,
};

type InvoicePaymentViewProps = {
  invoice: InvoiceBasic;
};

const InvoicePaymentView: FC<InvoicePaymentViewProps> = ({ invoice }) => {
  const auth = useAuth();
  const form = useForm<FormValues>();
  const lang = useLang();
  const modal = useModal();
  const navigate = useNavigate();
  const params = useParams();

  const [pending, setPending] = useState(false);
  const [trxRef, setTrxRef] = useState('');

  const currentMethod = useFormWatch('method', form);

  const invoiceDisplayId = params.displayId as string;

  const paymentStatusQuery = useQuery({
    queryKey: [config.PAYMENT_STATUS_QUERY_KEY, trxRef],
    queryFn: () => trxRef ? paymentService.getPaymentStatus(trxRef) : null,
    enabled: Boolean(trxRef),
    refetchInterval: config.PAYMENT_STATUS_QUERY_INTERVAL,
  });

  const createCardPaymentMutation = useMutation({
    mutationFn: paymentService.createCardPayment,
  });

  const createFrancophonePaymentMutation = useMutation({
    mutationFn: paymentService.createFrancophonePayment,
  });

  const handleCardPayment = async (values: CardFormValues) => {
    const expiry = cardValidator.expirationDate(values.expiry);

    const response = await createCardPaymentMutation.mutateAsync({
      ...values,
      invoiceId: invoiceDisplayId,
      expiry_month: expiry.month as string,
      expiry_year: expiry.year as string,
    });

    setTrxRef(response.trxRef);
  };

  const handleFrancophonePayment = async (values: FrancophoneFormValues) => {
    const response = await createFrancophonePaymentMutation.mutateAsync({
      ...values,
      invoiceId: invoiceDisplayId,
    });

    setTrxRef(response.trxRef);
  };

  const handleSubmit = async (values: FormValues) => {
    switch (values.method) {
      case PaymentMethod.CARD:
        return handleCardPayment({ ...values, ...values.details });
      case PaymentMethod.FRANCOPHONE:
        return handleFrancophonePayment({ ...values, ...values.details });
      default:
        throw new Error('Method not implemented.');
    }
  };

  const handleModalSuccess = () => {
    setPending(true);
  };

  useEffect(() => {
    if (paymentStatusQuery.data) {
      const redirectUrl = paymentStatusQuery.data.redirectUrl;

      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        modal.show();
      }

      setPending(false);
    }
  }, [modal, navigate, paymentStatusQuery.data]);

  useEffect(() => {
    form.setFieldsValue(defaults({}, auth.profile, initialValues));
  }, [auth.profile, form]);

  useEffect(() => {
    if (form.isFieldTouched('method')) {
      form.resetFields(['details']);
    }
  }, [form, currentMethod]);

  const waiting = Boolean(trxRef);
  const loading = waiting || createCardPaymentMutation.isPending || createFrancophonePaymentMutation.isPending;
  const formattedAmount = formatter.formatCurrency(invoice.amount, invoice.currency);
  const paymentError = paymentStatusQuery.data?.errorMessage;
  const paymentStatus = paymentStatusQuery.data?.status;
  const ModalComponent = paymentStatus && modals[paymentStatus];

  return (
    <BaseLayout title={lang.get('invoicePayment.title', { id: invoiceDisplayId })}>
      <div className={styles.root}>
        <Container className={styles.container}>
          <Card>

            <div className={styles.header}>
              <Link className={styles.header__logo} to={url.toDashboard()}>
                <Logo />
              </Link>
              <div className={styles.header__amount}>{formattedAmount}</div>
            </div>

            <Form
              className={styles.form}
              form={form}
              initialValues={initialValues}
              validationSchema={validationSchema as Schema}
              hidden={waiting}
              onFinish={handleSubmit}
            >
              <Form.Item name="method" label={lang.get('common.form.paymentMethod.label')}>
                <Select
                  placeholder={lang.get('common.form.paymentMethod.placeholder')}
                  options={paymentMethods.map((method) => ({
                    value: method,
                    label: lang.get(`common.paymentMethods.${method.toLowerCase()}`),
                  }))}
                  sort
                />
              </Form.Item>
              <Form.Item hidden={currentMethod !== PaymentMethod.CARD}>
                <Card>
                  <Form.Item name={['details', 'card_number']} label={lang.get('common.form.cardNumber.label')}>
                    <Input.CardNumber />
                  </Form.Item>
                  <Form.Columns className={styles.card__columns}>
                    <Form.Item
                      name={['details', 'expiry']}
                      label={lang.get('common.form.cardExpiry.label')}
                    >
                      <Input.CardExpiry />
                    </Form.Item>
                    <Form.Item
                      name={['details', 'cvv']}
                      label={lang.get('common.form.cardCode.label')}
                    >
                      <Input.CardCode />
                    </Form.Item>
                  </Form.Columns>
                </Card>
              </Form.Item>
              <Form.Item
                name={['details', 'phone']}
                label={lang.get('common.form.phone.label')}
                hidden={currentMethod !== PaymentMethod.FRANCOPHONE}
              >
                <Input.Phone placeholder={lang.get('common.form.phone.placeholder')} />
              </Form.Item>
              <Form.Item name="email" label={lang.get('common.form.email.label')}>
                <Input.Email placeholder={lang.get('common.form.email.placeholder')} />
              </Form.Item>
              <Form.Item name="purpose" label={lang.get('common.form.notes.label')}>
                <TextArea placeholder={lang.get('common.form.notes.placeholder')} />
              </Form.Item>
              <Button htmlType="submit" block loading={loading}>
                {lang.get('invoicePayment.submit', { amount: formattedAmount })}
              </Button>
            </Form>

            <Spin className={styles.spin} spinning={waiting} />

            {ModalComponent && (
              <ModalComponent
                trxRef={trxRef}
                error={paymentError}
                open={modal.open}
                pending={pending}
                onCancel={modal.hide}
                onSuccess={handleModalSuccess}
              />
            )}

          </Card>
        </Container>
      </div>
    </BaseLayout>
  );
};

export default InvoicePaymentView;
